/**
 * Adding a table, or changing one.
 *
 * WHY THIS EXISTS
 * ---------------
 * The branch has always been able to do both — the endpoints, the validation
 * and the guards were all written — and no screen ever called them. The only
 * way a table had ever been created was a setup script, so a restaurant that
 * bought another four chairs, or moved a two-seater onto the terrace, had no
 * way to say so.
 *
 * WHAT THE BRANCH REFUSES, AND WHY IT IS WORTH KNOWING HERE
 * ---------------------------------------------------------
 * Two tables with the same number, because that ambiguity is the whole thing a
 * table number exists to remove; and taking a table off the floor while people
 * are still sitting at it, because the order would go on pointing at it and
 * the party would vanish from the screen mid-meal.
 *
 * Both are checked again at the branch. Neither is guessed at here — the
 * message that comes back is shown as it is written, because it names the
 * table or the order in the way somebody can act on.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { api } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';

/** The parts of a floor this system knows about. */
const AREAS = [
  { value: 'main', label: 'Main floor' },
  { value: 'outdoor', label: 'Outdoor' },
  { value: 'terrace', label: 'Terrace' },
  { value: 'private', label: 'Private room' },
  { value: 'counter', label: 'Counter' },
];

const field =
  'h-10 rounded-lg border border-rule bg-surface-3 px-2 text-sm text-ink focus:border-brand-400 focus:outline-none';

export function TableForm({ table, onClose, onSaved }) {
  const editing = Boolean(table?.id);

  const [number, setNumber] = useState(table?.number ?? '');
  const [seats, setSeats] = useState(String(table?.seats ?? 4));
  const [area, setArea] = useState(table?.area ?? 'main');

  const save = useMutation({
    mutationFn: () => {
      const body = { number: String(number).trim(), seats: Number(seats), area };
      return editing
        ? api.put(`/pos/tables/${table.id}`, body)
        : api.post('/pos/tables', body);
    },
    onSuccess: onSaved,
  });

  /**
   * Removing a table. The branch decides how: one that never had an order is
   * deleted outright; one with a bill in its past is taken off the floor
   * instead, because those bills still point at it. Either way the tile goes.
   */
  const remove = useMutation({
    mutationFn: () => api.delete(`/pos/tables/${table.id}`),
    onSuccess: onSaved,
  });

  const problem = save.error ?? remove.error;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={editing ? `Edit table ${table.number}` : 'Add a table'}
      className="fixed inset-0 z-30 flex items-end justify-center bg-black/50 p-3 sm:items-center"
    >
      <div className="w-full max-w-sm rounded-2xl bg-surface p-5 shadow-xl">
        <h2 className="mb-4 font-medium text-ink">
          {editing ? `Table ${table.number}` : 'Add a table'}
        </h2>

        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-sm text-ink-2">Number or name</span>
            <input
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              placeholder="7, or Window"
              aria-label="Table number or name"
              className={field}
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm text-ink-2">Seats</span>
            <input
              type="number"
              min="1"
              value={seats}
              onChange={(e) => setSeats(e.target.value)}
              aria-label="Seats"
              className={`${field} tabular-nums`}
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm text-ink-2">Where it is</span>
            <select
              value={area}
              onChange={(e) => setArea(e.target.value)}
              aria-label="Where it is"
              className={field}
            >
              {AREAS.map((a) => (
                <option key={a.value} value={a.value}>
                  {a.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {problem && (
          <p className="mt-3 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
            {/* Shown as the branch wrote it: it names the clashing table or the
                bill still on this one, which is what somebody needs to act. */}
            {problem.details?.join(' ') ?? problem.message ?? 'That did not work.'}
          </p>
        )}

        <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
          {editing && (
            <button
              onClick={() => {
                if (window.confirm(`Delete table ${table.number}?`)) remove.mutate();
              }}
              disabled={remove.isPending}
              className="mr-auto rounded-lg px-2.5 py-1.5 text-sm text-bad hover:bg-surface-3 disabled:opacity-40"
            >
              {remove.isPending ? 'Deleting…' : 'Delete table'}
            </button>
          )}

          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => save.mutate()}
            disabled={!String(number).trim() || !(Number(seats) > 0) || save.isPending}
          >
            {save.isPending ? 'Saving' : editing ? 'Save' : 'Add it'}
          </Button>
        </div>
      </div>
    </div>
  );
}
